/*
  Discount Materials trade-pricing & UX scripts.
  Defines:
    <vat-toggle>      — switches Inc/Ex VAT site-wide, persisted via cookie
    <sticky-atc>      — sticky add-to-cart bar visible after main ATC scrolls off
    <back-to-top>     — floating button that appears once user scrolls past 600px
    <recently-viewed> — reads localStorage list, fetches /products/{handle}.js, renders cards
*/

const DBM = window.DBM || {};
window.DBM = DBM;

DBM.cookie = {
  get(name) {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  },
  set(name, value, days = 365) {
    const expires = new Date(Date.now() + days * 86400000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; samesite=lax`;
  },
};

DBM.formatMoney = (pence, currency = 'GBP') => {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
    }).format(pence / 100);
  } catch (_e) {
    return `£${(pence / 100).toFixed(2)}`;
  }
};

/* ------------------------------------------------------------------ */
/* Shared AJAX add-to-cart used by both sticky-atc and predictive    */
/* search. Refreshes cart-drawer DOM, fires pub-sub, opens drawer.   */
/*                                                                    */
/* Serialized via a single mutex Promise so two rapid clicks (sticky  */
/* + predictive at once) can't interleave drawer rebuilds, double-    */
/* publish events, or create a half-rendered drawer DOM.              */
/* ------------------------------------------------------------------ */
DBM._addToCartMutex = Promise.resolve();

DBM.addToCart = function (params) {
  // Chain on the previous call so requests run strictly serial.
  const next = DBM._addToCartMutex.then(
    () => DBM._addToCartImpl(params),
    () => DBM._addToCartImpl(params) // recover if previous call rejected
  );
  // Keep the chain alive even if this call rejects, so the next caller still serializes.
  DBM._addToCartMutex = next.catch(() => {});
  return next;
};

/* Theme sections re-rendered server-side with every cart mutation. */
DBM._cartSections = [
  'cart-drawer',
  'cart-icon-bubble',
  'main-cart-items',
  'cart-live-region-text',
  'main-cart-footer',
];

DBM._addToCartImpl = async function ({ variantId, quantity = 1, source = 'dbm', openDrawer = false }) {
  const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/javascript',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({
      items: [{ id: parseInt(variantId, 10), quantity }],
      sections: DBM._cartSections.join(','),
      sections_url: window.location.pathname,
    }),
  });

  if (!res.ok) {
    const errorPayload = await res.json().catch(() => null);
    throw new Error(errorPayload?.description || `HTTP ${res.status}`);
  }

  const data = await res.json();
  const cart = await DBM._syncCartDom(data, source, variantId);

  // The cart drawer is only opened when the caller explicitly opts in
  // (openDrawer: true). Default behaviour is silent — every caller
  // surface (predictive-search, sticky ATC, FBT, saved items, PDP)
  // already shows its own inline "Added \u2713" confirmation, so
  // popping the drawer over the top fights conversion.
  if (openDrawer) {
    document.querySelectorAll('predictive-search[open], predictive-search[results]').forEach((c) => {
      if (typeof c.close === 'function') c.close(true);
      else {
        c.removeAttribute('open');
        c.removeAttribute('results');
        c.removeAttribute('loading');
      }
    });
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  return cart || data;
};

/* ------------------------------------------------------------------ */
/* Change the quantity of a variant already in the cart.              */
/* Serialised on the same mutex as addToCart so cart requests never   */
/* interleave. quantity: 0 removes the line. Returns the fresh cart.  */
/* Note: Shopify caps the quantity at available stock — read the      */
/* returned cart for the real resulting quantity.                     */
/* ------------------------------------------------------------------ */
DBM.updateCartQty = function (params) {
  const next = DBM._addToCartMutex.then(
    () => DBM._updateCartQtyImpl(params),
    () => DBM._updateCartQtyImpl(params)
  );
  DBM._addToCartMutex = next.catch(() => {});
  return next;
};

DBM._updateCartQtyImpl = async function ({ variantId, quantity, source = 'dbm' }) {
  const res = await fetch((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/javascript',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({
      updates: { [String(variantId)]: quantity },
      sections: DBM._cartSections.join(','),
      sections_url: window.location.pathname,
    }),
  });

  if (!res.ok) {
    const errorPayload = await res.json().catch(() => null);
    throw new Error(errorPayload?.description || `HTTP ${res.status}`);
  }

  const data = await res.json();
  const cart = await DBM._syncCartDom(data, source, variantId);
  return cart || data;
};

/* Shared post-cart-mutation work: refresh drawer + icon bubble DOM in
   place, fetch the fresh cart payload, publish cartUpdate, update the
   mobile badges. Returns the cart payload (or null). */
DBM._syncCartDom = async function (data, source, variantId) {
  // Refresh cart-drawer DOM IN PLACE (do NOT call drawer.renderContents() —
  // Shopify's CartDrawer.renderContents() auto-fires this.open() at the end,
  // which would slide the drawer in over the page. We want the drawer fresh
  // for when the customer chooses to open it themselves, but completely
  // silent in the meantime.
  if (data.sections && data.sections['cart-drawer']) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      try {
        const tmp = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
        const incoming = tmp.querySelector('cart-drawer');
        if (incoming) {
          drawer.innerHTML = incoming.innerHTML;
          if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
        }
      } catch (_e) { /* non-fatal */ }
    }
  }

  // Refresh cart icon bubble
  if (data.sections && data.sections['cart-icon-bubble']) {
    const bubbleHtml = new DOMParser()
      .parseFromString(data.sections['cart-icon-bubble'], 'text/html')
      .querySelector('.shopify-section');
    const bubbleNode = document.querySelector('#cart-icon-bubble');
    if (bubbleHtml && bubbleNode) bubbleNode.innerHTML = bubbleHtml.innerHTML;
  }

  // Get fresh cart payload for pub-sub subscribers
  const cartRes = await fetch((window.routes && window.routes.cart_url || '/cart') + '.js', {
    headers: { Accept: 'application/json' },
  }).catch(() => null);
  const cart = cartRes && cartRes.ok ? await cartRes.json().catch(() => null) : null;

  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source,
      cartData: cart || data,
      variantId: parseInt(variantId, 10),
    });
  }

  // Mobile bottom-nav badge fallback
  if (cart) {
    document.querySelectorAll('[data-mobile-cart-count]').forEach((badge) => {
      badge.textContent = String(Math.min(cart.item_count, 99));
      badge.hidden = cart.item_count === 0;
    });
    document.querySelectorAll('.dbm-badge').forEach((badge) => {
      badge.textContent = String(Math.min(cart.item_count, 99));
    });
  }

  return cart;
};

/* ------------------------------------------------------------------ */
/* VAT toggle                                                         */
/* ------------------------------------------------------------------ */
if (!customElements.get('vat-toggle')) {
  customElements.define(
    'vat-toggle',
    class VatToggle extends HTMLElement {
      connectedCallback() {
        // Options must be queried here — constructor runs before children exist
        // when the element is created via innerHTML (e.g. PDP variant swap).
        if (this._vatBound) return;
        this._vatBound = true;
        this.defaultMode = this.dataset.default || 'inc';

        this.addEventListener('click', (event) => {
          const btn = event.target.closest('[data-vat]');
          if (!btn || !this.contains(btn)) return;
          event.preventDefault();
          const mode = btn.dataset.vat;
          if (!mode) return;
          DBM.cookie.set('dbm_vat_mode', mode);
          DBM.applyVatMode(mode);
        });

        const saved = DBM.cookie.get('dbm_vat_mode');
        const initial = saved || this.defaultMode;
        DBM.applyVatMode(initial);
      }
    }
  );
}

/* Returns the pence amount adjusted for the current VAT mode.
   Shopify prices are stored inc-VAT; ex-VAT divides out the configured rate. */
DBM.vatAdjust = (pence) => {
  if (document.documentElement.dataset.vatMode !== 'ex') return pence;
  const rate = parseFloat(document.documentElement.dataset.vatRate || '20') / 100;
  return Math.round(pence / (1 + rate));
};

DBM.formatMoneyVat = (pence) => {
  const adjusted = DBM.vatAdjust(pence);
  const withCode = document.documentElement.dataset.currencyCode === 'true';
  const formatted = DBM.formatMoney(adjusted);
  if (!withCode) return formatted;
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
  // Avoid duplicating if formatMoney already included a code somehow
  if (/\b[A-Z]{3}\b/.test(formatted)) return formatted;
  return `${formatted} ${currency}`;
};

DBM.applyVatMode = (mode, _scope) => {
  const isInc = mode === 'inc';
  document.documentElement.dataset.vatMode = mode;

  // Guard so the DOM-swap MutationObserver below ignores our own price
  // rewrites (observer callbacks run as microtasks, before this timeout).
  DBM._vatApplying = true;
  setTimeout(() => {
    DBM._vatApplying = false;
  }, 0);

  document.querySelectorAll('vat-toggle .vat-toggle__option').forEach((btn) => {
    btn.classList.toggle('is-active', btn.dataset.vat === mode);
    btn.setAttribute('aria-pressed', btn.dataset.vat === mode ? 'true' : 'false');
  });

  document.querySelectorAll('[data-price-pence]').forEach((el) => {
    const pence = parseInt(el.dataset.pricePence, 10);
    if (Number.isNaN(pence)) return;
    el.textContent = DBM.formatMoneyVat(pence);
  });

  // Only the dedicated label spans — never price elements — get label text.
  document.querySelectorAll('[data-vat-label]').forEach((el) => {
    if (el.hasAttribute('data-price-pence')) return;
    el.textContent = isInc ? 'inc VAT' : 'ex VAT';
  });

  // Let derived displays (e.g. FBT bundle total) recompute in the new mode.
  document.dispatchEvent(new CustomEvent('dbm:vat-changed', { detail: { mode } }));
};

/* Re-applies the saved (or default) mode to the whole page. Used after any
   DOM swap that re-renders server HTML with inc-VAT prices: PDP variant
   change, cart-drawer rebuild after add-to-cart, drawer quantity updates. */
DBM.reapplyVatMode = () => {
  const mode =
    DBM.cookie.get('dbm_vat_mode') ||
    document.documentElement.dataset.vatMode ||
    'inc';
  DBM.applyVatMode(mode);
};

document.addEventListener('DOMContentLoaded', () => {
  const saved = DBM.cookie.get('dbm_vat_mode');
  if (saved) DBM.applyVatMode(saved);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    // PDP price block is replaced with fresh server HTML on variant change;
    // cart drawer / cart page sections are re-rendered on cart updates.
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      // Defer so product-info.js finishes writing innerHTML first
      requestAnimationFrame(() => DBM.reapplyVatMode());
    });
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => DBM.reapplyVatMode());
  }

  // Predictive-search dropdowns, faceted grids, and the PDP price block are
  // re-rendered with fresh server HTML (inc-VAT prices) outside pub-sub, so
  // watch them and re-apply the saved mode. The _vatApplying guard stops
  // our own rewrites from re-triggering the observer.
  const vatSwapObserver = new MutationObserver(() => {
    if (DBM._vatApplying) return;
    clearTimeout(DBM._vatSwapTimer);
    DBM._vatSwapTimer = setTimeout(() => DBM.reapplyVatMode(), 60);
  });
  document.querySelectorAll('predictive-search').forEach((el) => {
    vatSwapObserver.observe(el, { childList: true, subtree: true });
  });
  const facetGrid = document.getElementById('ProductGridContainer');
  if (facetGrid) vatSwapObserver.observe(facetGrid, { childList: true, subtree: true });
  document.querySelectorAll('product-info, .product__info-container').forEach((el) => {
    vatSwapObserver.observe(el, { childList: true, subtree: true });
  });
});

/* ------------------------------------------------------------------ */
/* Sticky add-to-cart                                                 */
/* ------------------------------------------------------------------ */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyATC extends HTMLElement {
      constructor() {
        super();
        this.targetSelector = this.dataset.target || '.product__info-wrapper';
        this.button = this.querySelector('[data-sticky-atc-button]');
        this.priceEl = this.querySelector('[data-sticky-atc-price]');
        this.titleEl = this.querySelector('[data-sticky-atc-title]');
      }

      connectedCallback() {
        this.target = document.querySelector(this.targetSelector);
        if (!this.target) return;

        this.observer = new IntersectionObserver(
          ([entry]) => this.classList.toggle('is-visible', !entry.isIntersecting),
          { rootMargin: '-100px 0px 0px 0px' }
        );
        this.observer.observe(this.target);

        this.button?.addEventListener('click', (event) => this.handleClick(event));

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.variantUnsub?.();
      }

      async handleClick(event) {
        event.preventDefault();
        if (this.button.disabled) return;

        // Prefer the live product form if available — supports gift-card recipient,
        // line item properties, dynamic-checkout add. We submit it via requestSubmit()
        // so the <product-form> intercept fires correctly with the user's current qty.
        const form = this.target.querySelector('form[action*="/cart/add"]');
        const productForm = this.target.querySelector('product-form');

        if (form && productForm && typeof form.requestSubmit === 'function') {
          form.requestSubmit();
          return;
        }

        // Fallback: AJAX add via shared helper, respecting the live quantity input
        // and variant rules (min, increment) so trade buyers never silently get qty 1.
        const variantInput = (form && form.querySelector('input[name="id"]')) ||
          this.target.querySelector('input[name="id"]');
        const variantId = variantInput && variantInput.value;
        if (!variantId) return;

        const qtyInput =
          (form && form.querySelector('input[name="quantity"]')) ||
          this.target.querySelector('input[name="quantity"]');
        let quantity = 1;
        if (qtyInput) {
          const parsed = parseInt(qtyInput.value, 10);
          if (Number.isFinite(parsed) && parsed > 0) quantity = parsed;
          const min = parseInt(qtyInput.min, 10);
          if (Number.isFinite(min) && min > quantity) quantity = min;
        }

        const original = this.button.textContent;
        this.button.disabled = true;
        this.button.textContent = 'Adding\u2026';

        try {
          await DBM.addToCart({ variantId, quantity, source: 'sticky-atc' });
          this.button.textContent = `Added ${quantity > 1 ? quantity + ' ' : ''}\u2713`;
          setTimeout(() => {
            this.button.textContent = original;
            this.button.disabled = false;
          }, 1500);
        } catch (err) {
          console.error('[sticky-atc] add failed', err);
          this.button.textContent = 'Try again';
          setTimeout(() => {
            this.button.textContent = original;
            this.button.disabled = false;
          }, 1800);
        }
      }

      onVariantChange(event) {
        const variant = event?.data?.variant;
        if (!variant) return;
        if (this.priceEl) {
          this.priceEl.textContent = DBM.formatMoney(variant.price);
          this.priceEl.dataset.pricePence = variant.price;
        }
        if (this.button) {
          this.button.disabled = !variant.available;
          this.button.textContent = variant.available ? 'Add to basket' : 'Sold out';
        }
        const mode = DBM.cookie.get('dbm_vat_mode') || document.documentElement.dataset.vatMode;
        if (mode) DBM.applyVatMode(mode);
      }
    }
  );
}

/* ------------------------------------------------------------------ */
/* Back to top                                                        */
/* ------------------------------------------------------------------ */
if (!customElements.get('back-to-top')) {
  customElements.define(
    'back-to-top',
    class BackToTop extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('button');
      }
      connectedCallback() {
        this.onScroll = () => this.classList.toggle('is-visible', window.scrollY > 600);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
        this.button?.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
      }
      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
      }
    }
  );
}

/* ------------------------------------------------------------------ */
/* Recently viewed                                                    */
/* ------------------------------------------------------------------ */
DBM.recentlyViewed = {
  key: 'dbm_recently_viewed',
  max: 8,
  read() {
    try {
      return JSON.parse(localStorage.getItem(this.key) || '[]');
    } catch (_e) {
      return [];
    }
  },
  write(list) {
    try {
      localStorage.setItem(this.key, JSON.stringify(list.slice(0, this.max)));
    } catch (_e) {
      /* quota / private mode */
    }
  },
  push(handle) {
    if (!handle) return;
    const list = this.read().filter((h) => h !== handle);
    list.unshift(handle);
    this.write(list);
  },
};

/* ------------------------------------------------------------------ */
/* Mobile bottom nav cart-bubble updates + universal cart drawer link */
/* ------------------------------------------------------------------ */
document.addEventListener('DOMContentLoaded', () => {
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      try {
        const count = event?.cartData?.item_count;
        if (typeof count !== 'number') return;
        document.querySelectorAll('[data-mobile-cart-count]').forEach((badge) => {
          badge.textContent = String(Math.min(count, 99));
          badge.hidden = count === 0;
        });
        // Keep mobile bottom-nav basket aria-label accurate for screen readers
        document.querySelectorAll('#MobileNavCart').forEach((link) => {
          link.setAttribute('aria-label', `Basket (${count})`);
        });
      } catch (e) {
        // Fail-soft: never let a UI subscriber break other subscribers
        console.error('[dbm] cart subscriber error', e);
      }
    });
  }

  // Mobile bottom-nav "Menu" button → opens the new dbm-mega-menu drawer.
  // The actual handler is now defined inside snippets/dbm-mega-menu.liquid
  // (it lives next to the drawer DOM so the open/close state stays in sync
  // with the rest of the mega-menu component). This block is intentionally
  // empty — kept as a marker so the wiring isn't accidentally re-added here.

  // Universal: any link to /cart that isn't explicitly opted-out opens the cart drawer
  // when one exists. Falls back to navigating to the cart page if no drawer is on page.
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('a[href]');
    if (!link) return;
    const href = link.getAttribute('href') || '';
    if (link.dataset.openCartDrawer === 'false') return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
    if (link.target && link.target !== '' && link.target !== '_self') return;

    const isCartLink =
      href === '/cart' ||
      href.endsWith('/cart') ||
      href === (window.routes && window.routes.cart_url);
    if (!isCartLink) return;

    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      event.preventDefault();
      drawer.open(link);
    }
  });
});

/* ------------------------------------------------------------------ */
/* Idle prefetch on link hover (instant.page-style)                   */
/* Throttled so we don't spam the network on long hover-heavy pages,  */
/* and disabled entirely on slow connections / data-saver mode.       */
/* ------------------------------------------------------------------ */
(function () {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (conn && (conn.saveData || /^([23])g$/.test(conn.effectiveType || ''))) return;

  const prefetched = new Set();
  const HOVER_DELAY = 65;
  const MAX_CONCURRENT = 2;
  let inFlight = 0;
  let pendingTimer = null;

  const sameOrigin = (url) => {
    try {
      return new URL(url, location.href).origin === location.origin;
    } catch (_e) {
      return false;
    }
  };

  const prefetch = (href) => {
    if (prefetched.has(href) || inFlight >= MAX_CONCURRENT) return;
    prefetched.add(href);
    inFlight++;
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    link.as = 'document';
    link.onload = link.onerror = () => {
      inFlight = Math.max(0, inFlight - 1);
    };
    document.head.appendChild(link);
  };

  const onPointerEnter = (event) => {
    const a = event.target.closest && event.target.closest('a[href]');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
    if (a.target && a.target !== '' && a.target !== '_self') return;
    if (a.dataset.noprefetch === 'true') return;
    if (!sameOrigin(href)) return;
    if (/\.(jpg|jpeg|png|webp|svg|gif|pdf|zip|mp4|mov)$/i.test(href)) return;

    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(() => prefetch(a.href), HOVER_DELAY);
  };

  const onPointerLeave = () => {
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
  };

  document.addEventListener('pointerenter', onPointerEnter, { capture: true, passive: true });
  document.addEventListener('pointerleave', onPointerLeave, { capture: true, passive: true });
})();

/* ------------------------------------------------------------------ */
/* Welcome-back ribbon (homepage only)                                */
/* Shows recent searches as quick chips for returning visitors.       */
/* ------------------------------------------------------------------ */
if (!customElements.get('welcome-back-ribbon')) {
  customElements.define(
    'welcome-back-ribbon',
    class WelcomeBackRibbon extends HTMLElement {
      connectedCallback() {
        const dismissed = DBM.cookie.get('dbm_welcome_dismissed');
        if (dismissed) return;

        let recent = [];
        try {
          recent = JSON.parse(localStorage.getItem('dbm_recent_searches') || '[]');
        } catch (_e) {
          recent = [];
        }
        if (!recent.length) return;

        const chipsEl = this.querySelector('[data-welcome-back-chips]');
        if (!chipsEl) return;

        chipsEl.innerHTML = recent
          .slice(0, 3)
          .map(
            (term) =>
              `<a class="welcome-back__chip" href="/search?q=${encodeURIComponent(term)}">${String(term)
                .replace(/[<>"&]/g, (ch) =>
                  ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : ch === '"' ? '&quot;' : '&amp;'
                )}</a>`
          )
          .join('');

        // Show after a short delay so it doesn't yank LCP
        setTimeout(() => {
          this.hidden = false;
          this.classList.add('is-visible');
        }, 800);

        const closeBtn = this.querySelector('[data-welcome-back-close]');
        closeBtn?.addEventListener('click', () => {
          this.classList.remove('is-visible');
          DBM.cookie.set('dbm_welcome_dismissed', '1', 30);
          setTimeout(() => {
            this.hidden = true;
          }, 200);
        });
      }
    }
  );
}

/* ------------------------------------------------------------------ */
/* Recently viewed                                                    */
/* Uses textContent + element creation (XSS-safe) instead of innerHTML*/
/* and builds image URLs via the URL API so it works whether or not   */
/* the CDN URL already has query params.                              */
/* ------------------------------------------------------------------ */
DBM.cdnImageUrl = function (rawUrl, width) {
  if (!rawUrl) return '';
  try {
    const u = new URL(rawUrl, location.origin);
    u.searchParams.set('width', String(width));
    return u.toString();
  } catch (_e) {
    const sep = rawUrl.indexOf('?') === -1 ? '?' : '&';
    return `${rawUrl}${sep}width=${width}`;
  }
};

if (!customElements.get('recently-viewed')) {
  customElements.define(
    'recently-viewed',
    class RecentlyViewed extends HTMLElement {
      connectedCallback() {
        if (!window.DBM?.recentlyViewed) return;
        const currentHandle = this.dataset.currentHandle;
        const handles = DBM.recentlyViewed.read().filter((h) => h !== currentHandle).slice(0, 6);
        if (!handles.length) {
          this.hidden = true;
          return;
        }

        const row = this.querySelector('[data-recently-viewed-row]');
        if (!row) return;

        // Defer fetches until idle so they never compete with LCP.
        const runFetches = () => {
          Promise.all(
            handles.map((handle) =>
              fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
                .then((r) => (r.ok ? r.json() : null))
                .catch(() => null)
            )
          ).then((products) => {
            const fragment = document.createDocumentFragment();
            products.filter(Boolean).forEach((product) => {
              const a = document.createElement('a');
              a.className = 'recently-viewed__card';
              a.href = product.url;

              const img = document.createElement('img');
              img.className = 'recently-viewed__card-image';
              img.loading = 'lazy';
              img.width = 160;
              img.height = 160;
              img.alt = product.title || '';
              if (product.featured_image) {
                img.src = DBM.cdnImageUrl(product.featured_image, 320);
              }
              a.appendChild(img);

              const titleEl = document.createElement('p');
              titleEl.className = 'recently-viewed__card-title';
              titleEl.textContent = product.title || '';
              a.appendChild(titleEl);

              const priceEl = document.createElement('p');
              priceEl.className = 'recently-viewed__card-price';
              priceEl.textContent = DBM.formatMoney(product.price);
              a.appendChild(priceEl);

              fragment.appendChild(a);
            });
            row.replaceChildren(fragment);
            this.hidden = false;
          });
        };

        if ('requestIdleCallback' in window) {
          requestIdleCallback(runFetches, { timeout: 2000 });
        } else {
          setTimeout(runFetches, 1500);
        }
      }
    }
  );
}

/* ================================================================== */
/* SAVE-FOR-LATER                                                     */
/* localStorage list of product handles. Heart on cards/PDP toggles.  */
/* Mobile bottom-nav badge updated. Page section <saved-items-grid>   */
/* hydrates client-side using /products/{handle}.js fetches.          */
/* ================================================================== */
DBM.savedItems = {
  key: 'dbm_saved_items',
  read() {
    try {
      const raw = localStorage.getItem(this.key);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (_e) {
      return [];
    }
  },
  write(list) {
    try {
      localStorage.setItem(this.key, JSON.stringify(list.slice(0, 100)));
      this.notify();
    } catch (_e) {
      /* quota / private mode */
    }
  },
  has(handle) {
    return this.read().indexOf(handle) !== -1;
  },
  toggle(handle) {
    const list = this.read();
    const idx = list.indexOf(handle);
    if (idx === -1) list.unshift(handle);
    else list.splice(idx, 1);
    this.write(list);
    return idx === -1; // returns true if NOW saved
  },
  notify() {
    document.dispatchEvent(new CustomEvent('dbm:saved-changed', { detail: { count: this.read().length } }));
    this.refreshBadge();
  },
  refreshBadge() {
    const count = this.read().length;
    document.querySelectorAll('[data-saved-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-dbm-save]').forEach((btn) => {
      const saved = this.has(btn.dataset.productHandle);
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    });
  },
};

document.addEventListener('DOMContentLoaded', () => DBM.savedItems.refreshBadge());

document.addEventListener('click', (event) => {
  const btn = event.target.closest && event.target.closest('[data-dbm-save]');
  if (!btn) return;
  event.preventDefault();
  const handle = btn.dataset.productHandle;
  if (!handle) return;
  const nowSaved = DBM.savedItems.toggle(handle);
  btn.classList.toggle('is-saved', nowSaved);
  btn.setAttribute('aria-pressed', nowSaved ? 'true' : 'false');
  // tiny haptic: pulse
  btn.style.transform = 'scale(0.92)';
  setTimeout(() => (btn.style.transform = ''), 120);
});

if (!customElements.get('saved-items-grid')) {
  customElements.define(
    'saved-items-grid',
    class SavedItemsGrid extends HTMLElement {
      constructor() {
        super();
        this.list = this.querySelector('[data-saved-list]');
        this.empty = this.querySelector('[data-saved-empty]');
        this.actions = this.querySelector('[data-saved-actions]');
      }
      connectedCallback() {
        this.render();
        document.addEventListener('dbm:saved-changed', () => this.render());
        this.querySelector('[data-saved-clear]')?.addEventListener('click', () => {
          if (!confirm('Clear all saved items?')) return;
          DBM.savedItems.write([]);
        });
        this.querySelector('[data-saved-add-all]')?.addEventListener('click', () => this.addAll());
      }
      async render() {
        const handles = DBM.savedItems.read();
        if (!handles.length) {
          this.list.replaceChildren();
          this.empty.hidden = false;
          this.actions.hidden = true;
          return;
        }
        this.empty.hidden = true;
        this.actions.hidden = false;
        const products = await Promise.all(
          handles.map((h) =>
            fetch(`/products/${encodeURIComponent(h)}.js`, { headers: { Accept: 'application/json' } })
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          )
        );
        const frag = document.createDocumentFragment();
        products.filter(Boolean).forEach((p) => {
          const card = document.createElement('article');
          card.className = 'dbm-saved-card';
          const a = document.createElement('a');
          a.href = p.url;
          a.className = 'dbm-saved-card__link';
          if (p.featured_image) {
            const img = document.createElement('img');
            img.className = 'dbm-saved-card__image';
            img.src = DBM.cdnImageUrl(p.featured_image, 400);
            img.alt = p.title || '';
            img.loading = 'lazy';
            img.width = 200;
            img.height = 200;
            a.appendChild(img);
          }
          const info = document.createElement('div');
          info.className = 'dbm-saved-card__info';
          const title = document.createElement('h3');
          title.className = 'dbm-saved-card__title';
          title.textContent = p.title || '';
          const price = document.createElement('p');
          price.className = 'dbm-saved-card__price';
          price.textContent = DBM.formatMoney(p.price);
          info.appendChild(title);
          info.appendChild(price);
          a.appendChild(info);
          card.appendChild(a);

          const actions = document.createElement('div');
          actions.className = 'dbm-saved-card__actions';
          const addBtn = document.createElement('button');
          addBtn.type = 'button';
          addBtn.className = 'dbm-saved-card__add';
          addBtn.textContent = 'Add to basket';
          addBtn.addEventListener('click', async () => {
            try {
              addBtn.disabled = true;
              addBtn.textContent = 'Adding\u2026';
              await DBM.addToCart({ variantId: p.variants[0].id, quantity: 1, source: 'saved-items' });
              addBtn.textContent = 'Added \u2713';
              setTimeout(() => {
                addBtn.disabled = false;
                addBtn.textContent = 'Add to basket';
              }, 1200);
            } catch (_e) {
              addBtn.textContent = 'Try again';
              setTimeout(() => {
                addBtn.disabled = false;
                addBtn.textContent = 'Add to basket';
              }, 1500);
            }
          });
          const removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'dbm-saved-card__remove';
          removeBtn.setAttribute('aria-label', `Remove ${p.title} from saved`);
          removeBtn.innerHTML =
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
          removeBtn.addEventListener('click', () => DBM.savedItems.toggle(p.handle));
          actions.appendChild(addBtn);
          actions.appendChild(removeBtn);
          card.appendChild(actions);

          frag.appendChild(card);
        });
        this.list.replaceChildren(frag);
      }
      async addAll() {
        const handles = DBM.savedItems.read();
        for (const h of handles) {
          const p = await fetch(`/products/${encodeURIComponent(h)}.js`).then((r) => r.json()).catch(() => null);
          if (p && p.variants && p.variants[0]) {
            await DBM.addToCart({ variantId: p.variants[0].id, quantity: 1, source: 'saved-add-all' }).catch(() => {});
          }
        }
      }
    }
  );
}

/* ================================================================== */
/* FREQUENTLY BOUGHT TOGETHER bundle                                  */
/* Recomputes total when checkboxes toggle. Single button             */
/* sequentially adds each ticked variant to cart.                     */
/* ================================================================== */
if (!customElements.get('fbt-bundle')) {
  customElements.define(
    'fbt-bundle',
    class FbtBundle extends HTMLElement {
      connectedCallback() {
        this.totalEl = this.querySelector('[data-fbt-total]');
        this.addBtn = this.querySelector('[data-fbt-add]');
        this.querySelectorAll('[data-fbt-check]').forEach((cb) =>
          cb.addEventListener('change', () => this.recalc())
        );
        this.addBtn?.addEventListener('click', () => this.addBundle());
        this.onVatChanged = () => this.recalc();
        document.addEventListener('dbm:vat-changed', this.onVatChanged);
        this.recalc();
      }
      disconnectedCallback() {
        document.removeEventListener('dbm:vat-changed', this.onVatChanged);
      }
      recalc() {
        let total = 0;
        this.querySelectorAll('.dbm-fbt__item').forEach((row) => {
          const cb = row.querySelector('[data-fbt-check]');
          const priceEl = row.querySelector('[data-fbt-price]');
          if (cb && cb.checked && priceEl) {
            total += parseInt(priceEl.dataset.pricePence, 10) || 0;
          }
        });
        if (this.totalEl) this.totalEl.textContent = DBM.formatMoneyVat
          ? DBM.formatMoneyVat(total)
          : DBM.formatMoney(DBM.vatAdjust(total));
      }
      async addBundle() {
        if (this.addBtn.disabled) return;
        const original = this.addBtn.textContent;
        this.addBtn.disabled = true;
        this.addBtn.textContent = 'Adding bundle\u2026';
        try {
          const items = [];
          this.querySelectorAll('.dbm-fbt__item').forEach((row) => {
            const cb = row.querySelector('[data-fbt-check]');
            const variant = row.querySelector('[data-fbt-variant]');
            if (cb && cb.checked && variant && variant.value) {
              items.push(variant.value);
            }
          });
          for (const id of items) {
            await DBM.addToCart({ variantId: id, quantity: 1, source: 'fbt' });
          }
          this.addBtn.textContent = `Added ${items.length} \u2713`;
          setTimeout(() => {
            this.addBtn.disabled = false;
            this.addBtn.textContent = original;
          }, 1500);
        } catch (_e) {
          this.addBtn.textContent = 'Try again';
          setTimeout(() => {
            this.addBtn.disabled = false;
            this.addBtn.textContent = original;
          }, 1800);
        }
      }
    }
  );
}

/* ================================================================== */
/* POSTCODE delivery check (UK validation, ETA, no carrier API yet).  */
/* ================================================================== */
if (!customElements.get('dbm-postcode-check')) {
  customElements.define(
    'dbm-postcode-check',
    class PostcodeCheck extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-dbm-postcode-input]');
        this.btn = this.querySelector('[data-dbm-postcode-submit]');
        this.result = this.querySelector('[data-dbm-postcode-result]');
        this.form = this.querySelector('form');
        // Restore from cookie
        const saved = DBM.cookie.get('dbm_postcode');
        if (saved && this.input) {
          this.input.value = saved;
          this.runCheck(saved);
        }
        this.form?.addEventListener('submit', (e) => {
          e.preventDefault();
          this.runCheck(this.input.value);
        });
      }
      runCheck(raw) {
        const cleaned = String(raw || '').trim().toUpperCase().replace(/\s+/g, ' ');
        // UK postcode regex (loose but accurate for common formats)
        const ukRegex = /^([Gg][Ii][Rr] 0[Aa]{2})|^((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) {0,1}[0-9][A-Za-z]{2})$/;
        if (!ukRegex.test(cleaned)) {
          this.result.hidden = false;
          this.result.classList.remove('is-ok');
          this.result.classList.add('is-warn');
          this.result.innerHTML =
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg> That doesn\u2019t look like a valid UK postcode \u2014 please check.';
          return;
        }

        DBM.cookie.set('dbm_postcode', cleaned, 90);

        // Compute next-working-day window (Mon-Fri orders before 14:00, ship next day)
        const now = new Date();
        const cutoff = 14;
        let offsetDays = now.getHours() >= cutoff ? 2 : 1;
        const minDate = this.addBusinessDays(now, offsetDays);
        const maxDate = this.addBusinessDays(now, offsetDays + 2);
        const fmt = (d) =>
          new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).format(d);

        this.result.hidden = false;
        this.result.classList.remove('is-warn');
        this.result.classList.add('is-ok');
        this.result.innerHTML =
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="m5 12 5 5L20 7"/></svg> ' +
          `Free UK delivery to <strong>${cleaned}</strong> \u2014 estimated arrival <strong>${fmt(minDate)}</strong> to <strong>${fmt(maxDate)}</strong>.`;
      }
      addBusinessDays(start, days) {
        const d = new Date(start);
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          const wd = d.getDay();
          if (wd !== 0 && wd !== 6) added++;
        }
        return d;
      }
    }
  );
}

/* ================================================================== */
/* MOBILE COLLECTION TOOLBAR                                          */
/* "Filter" opens the existing facets drawer; "Sort" opens the sort   */
/* disclosure. Updates the active-filter count badge.                 */
/* ================================================================== */
document.addEventListener('click', (event) => {
  const btn = event.target.closest && event.target.closest('[data-mobile-coll-toolbar] [data-action]');
  if (!btn) return;
  const action = btn.dataset.action;
  if (action === 'open-filters') {
    const trigger =
      document.querySelector('.disclosure-has-popup.menu-opening') ||
      document.querySelector('.facets-container .disclosure summary, .mobile-facets__open, .menu-drawer__open');
    // The standard Dawn pattern: scroll to the facets aside and "open" mobile drawer
    const facets = document.querySelector('#main-collection-filters, #facets-mobile, .mobile-facets, .facets-vertical-form, .facets-form');
    if (facets) {
      facets.scrollIntoView({ behavior: 'smooth', block: 'start' });
      facets.querySelector('summary, .mobile-facets__open')?.click();
    }
  } else if (action === 'open-sort') {
    const sort = document.querySelector('.facet-filters__sort, [name="sort_by"], .mobile-facets__sort');
    if (sort) {
      sort.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (sort.tagName === 'SELECT') sort.focus();
    }
  }
});

// Update active-filter count badge from URL params
function updateMobileFilterCount() {
  try {
    const params = new URLSearchParams(window.location.search);
    let count = 0;
    for (const k of params.keys()) {
      if (k.startsWith('filter.')) count++;
    }
    document.querySelectorAll('[data-active-filter-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  } catch (_e) { /* noop */ }
}
document.addEventListener('DOMContentLoaded', updateMobileFilterCount);

/* ================================================================== */
/* CALCULATOR WIDGETS — tabs + live maths + upsell add-to-cart        */
/* ================================================================== */
DBM._calcQtys = {};

function dbmCalcPackQty(rawQty, unitsPer) {
  const per = parseFloat(unitsPer) || 1;
  const raw = parseFloat(rawQty) || 0;
  if (raw <= 0) return 0;
  return per <= 1 ? Math.ceil(raw) : Math.ceil(raw / per);
}

/** Pack size for the product this PDP calculator is on (25kg bag vs 850kg bulk). */
function dbmCalcProductPack(section) {
  const root = section.closest('.dbm-calc--pdp') || section;
  const kg = parseFloat(root.dataset.packKg) || 0;
  const bagKg = parseFloat(root.dataset.bagKg) || kg;
  const bagsPerListing = parseInt(root.dataset.packUnits, 10) || 1;
  const label = root.dataset.packLabel || 'units';
  const productType = (root.dataset.productType || '').toLowerCase();
  return { kg, bagKg, bagsPerListing, label, productType };
}

function dbmCalcPacksFromKg(kgNeeded, packKg) {
  const per = packKg > 0 ? packKg : 850;
  return Math.ceil((parseFloat(kgNeeded) || 0) / per);
}

function dbmCalcPackResultLine(packs, productPack) {
  const bags = packs * (productPack.bagsPerListing || 1);
  if (productPack.bagsPerListing > 1) {
    return `<span>You need: <strong>${packs.toLocaleString('en-GB')}</strong> ${productPack.label} <em>(${bags.toLocaleString('en-GB')} bags)</em></span>`;
  }
  return `<span>You need: <strong>${packs.toLocaleString('en-GB')}</strong> ${productPack.label}</span>`;
}

function dbmCalcApplyQtys(section, qtyMap) {
  DBM._calcQtys = { ...DBM._calcQtys, ...qtyMap };
  section.querySelectorAll('[data-calc-upsell]').forEach((row) => {
    const source = row.dataset.calcQtySource;
    const raw = qtyMap[source] || 0;
    const qty = dbmCalcPackQty(raw, row.dataset.calcUnitsPer);
    row.dataset.calcQty = String(qty);
    const label = row.querySelector('[data-calc-qty-label]');
    const display = row.querySelector('[data-calc-qty-display]');
    if (label && display) {
      if (qty > 0) {
        display.textContent = qty.toLocaleString('en-GB');
        label.hidden = false;
      } else {
        label.hidden = true;
      }
    }
  });
  section.querySelectorAll('[data-calc-upsells]').forEach((wrap) => {
    const items = wrap.querySelectorAll('[data-calc-upsell]');
    if (items.length === 0) {
      wrap.hidden = true;
      return;
    }
    wrap.hidden = false;
    const panel = wrap.dataset.calcPanel;
    const panelEl = section.querySelector(`[data-panel="${panel}"]`);
    const isActive = !panelEl || panelEl.classList.contains('is-active') || section.querySelector('.dbm-calc__panels--single');
    if (!isActive) return;
    const addAll = wrap.querySelector('[data-calc-add-all]');
    if (addAll) addAll.hidden = items.length < 2;
  });
}

function dbmRunCalc(section) {
  const qtyMap = {};
  const productPack = dbmCalcProductPack(section);
  const isPdp = Boolean(section.closest('.dbm-calc--pdp') || section.classList.contains('dbm-calc--pdp'));

  const bL = section.querySelector('[data-brick-length]');
  const bH = section.querySelector('[data-brick-height]');
  const bS = section.querySelector('[data-brick-skin]');
  const bR = section.querySelector('[data-brick-result]');
  if (bL && bH && bS && bR) {
    const area = (parseFloat(bL.value) || 0) * (parseFloat(bH.value) || 0);
    const perM2 = parseInt(bS.value, 10) || 60;
    const bricks = Math.ceil(area * perM2);
    const withWaste = Math.ceil(bricks * 1.1);
    qtyMap['brick-waste'] = withWaste;
    qtyMap['brick-raw'] = bricks;
    bR.innerHTML = `<span>Bricks needed: <strong>${bricks.toLocaleString('en-GB')}</strong></span><span>+10% wastage: <strong>${withWaste.toLocaleString('en-GB')}</strong></span>`;
  }

  const mA = section.querySelector('[data-mortar-area]');
  const mR = section.querySelector('[data-mortar-result]');
  if (mA && mR) {
    const area = parseFloat(mA.value) || 0;
    const sand = Math.ceil(area * 25);
    const cement = Math.ceil(area * 5);
    const sandBags850 = Math.ceil((sand * 1.1) / 850);
    const cementBags25 = Math.ceil((cement * 1.1) / 25);
    qtyMap['mortar-sand-kg'] = sand;
    qtyMap['mortar-sand-bags'] = sandBags850;
    qtyMap['mortar-cement-kg'] = cement;
    qtyMap['mortar-cement-bags'] = cementBags25;

    if (isPdp && productPack.kg) {
      const isCement = productPack.productType.includes('cement');
      const kgNeeded = isCement ? cement * 1.1 : sand * 1.1;
      const packs = dbmCalcPacksFromKg(kgNeeded, productPack.kg);
      qtyMap['product-packs'] = packs;
      mR.innerHTML =
        `<span>Sharp sand: <strong>${sand.toLocaleString('en-GB')}kg</strong></span>` +
        `<span>Cement: <strong>${cement.toLocaleString('en-GB')}kg</strong></span>` +
        dbmCalcPackResultLine(packs, productPack);
    } else {
      mR.innerHTML =
        `<span>Sharp sand: <strong>${sand.toLocaleString('en-GB')}kg</strong> (~${(sand / 850).toFixed(1)} bulk bag)</span>` +
        `<span>Cement: <strong>${cement.toLocaleString('en-GB')}kg</strong> (${Math.ceil(cement / 25)} \u00d7 25kg bag)</span>`;
    }
  }

  const cL = section.querySelector('[data-conc-length]');
  const cW = section.querySelector('[data-conc-width]');
  const cD = section.querySelector('[data-conc-depth]');
  const cR = section.querySelector('[data-conc-result]');
  if (cL && cW && cD && cR) {
    const vol = (parseFloat(cL.value) || 0) * (parseFloat(cW.value) || 0) * (parseFloat(cD.value) || 0);
    const ballast = Math.ceil(vol * 800);
    const cement = Math.ceil(vol * 150);
    qtyMap['conc-ballast-kg'] = ballast;
    qtyMap['conc-ballast-bags'] = Math.ceil((ballast * 1.1) / 850);
    qtyMap['conc-cement-kg'] = cement;
    qtyMap['conc-cement-bags'] = Math.ceil((cement * 1.1) / 25);

    if (isPdp && productPack.kg) {
      const isCement = productPack.productType.includes('cement');
      const kgNeeded = isCement ? cement * 1.1 : ballast * 1.1;
      const packs = dbmCalcPacksFromKg(kgNeeded, productPack.kg);
      qtyMap['product-packs'] = packs;
      cR.innerHTML =
        `<span>Volume: <strong>${vol.toFixed(2)} m\u00b3</strong></span>` +
        `<span>Ballast: <strong>${ballast.toLocaleString('en-GB')}kg</strong></span>` +
        `<span>Cement: <strong>${cement.toLocaleString('en-GB')}kg</strong></span>` +
        dbmCalcPackResultLine(packs, productPack);
    } else {
      cR.innerHTML =
        `<span>Volume: <strong>${vol.toFixed(2)} m\u00b3</strong></span>` +
        `<span>Ballast: <strong>${ballast.toLocaleString('en-GB')}kg</strong></span>` +
        `<span>Cement: <strong>${cement.toLocaleString('en-GB')}kg</strong></span>`;
    }
  }

  const pL = section.querySelector('[data-pav-length]');
  const pW = section.querySelector('[data-pav-width]');
  const pS = section.querySelector('[data-pav-slab]');
  const pSD = section.querySelector('[data-pav-sub-depth]');
  const pR = section.querySelector('[data-pav-result]');
  if (pL && pW && pS && pR) {
    const area = (parseFloat(pL.value) || 0) * (parseFloat(pW.value) || 0);
    const slabSize = parseFloat(pS.value) || 0.36;
    const slabs = Math.ceil((area / slabSize) * 1.1);
    qtyMap['pav-slabs'] = slabs;
    let subHtml = '';
    if (pSD) {
      const depthM = (parseFloat(pSD.value) || 0) / 1000;
      const subVol = area * depthM;
      const subTonnes = Math.ceil(subVol * 2.2 * 1.1);
      qtyMap['pav-sub-tonnes'] = subTonnes;
      qtyMap['pav-sub-bags'] = Math.ceil((subTonnes * 1000) / 850);
      subHtml = `<span>Sub-base MOT: <strong>${subTonnes.toLocaleString('en-GB')} tonnes</strong></span>`;
    }
    pR.innerHTML = `<span>Area: <strong>${area.toFixed(2)} m\u00b2</strong></span><span>Slabs (+10%): <strong>${slabs}</strong></span>${subHtml}`;
  }

  const aA = section.querySelector('[data-agg-area]');
  const aD = section.querySelector('[data-agg-depth]');
  const aR = section.querySelector('[data-agg-result]');
  if (aA && aD && aR) {
    const area = parseFloat(aA.value) || 0;
    const depthM = (parseFloat(aD.value) || 0) / 1000;
    const vol = area * depthM;
    const kgNeeded = Math.ceil(vol * 2200 * 1.1);
    const tonnes = Math.ceil(kgNeeded / 1000);
    const packKg = isPdp && productPack.kg ? productPack.kg : 850;
    const packs = dbmCalcPacksFromKg(kgNeeded, packKg);
    const packLabel = isPdp && productPack.label ? productPack.label : 'bulk bags (850kg)';
    qtyMap['agg-tonnes'] = tonnes;
    qtyMap['agg-bags'] = packs;
    qtyMap['product-packs'] = packs;
    aR.innerHTML =
      `<span>Volume: <strong>${vol.toFixed(2)} m\u00b3</strong></span>` +
      `<span>Weight (+10%): <strong>${kgNeeded.toLocaleString('en-GB')} kg</strong> (~${tonnes.toLocaleString('en-GB')} t)</span>` +
      (isPdp && productPack.kg
        ? dbmCalcPackResultLine(packs, productPack)
        : `<span>You need: <strong>${packs.toLocaleString('en-GB')}</strong> ${packLabel}</span>`);
  }

  const gA = section.querySelector('[data-grav-area]');
  const gD = section.querySelector('[data-grav-depth]');
  const gR = section.querySelector('[data-grav-result]');
  if (gA && gD && gR) {
    const area = parseFloat(gA.value) || 0;
    const depthM = (parseFloat(gD.value) || 0) / 1000;
    const vol = area * depthM;
    const kg = Math.ceil(vol * 1600 * 1.1);
    const packKg = isPdp && productPack.kg ? productPack.kg : 850;
    const packs = dbmCalcPacksFromKg(kg, packKg);
    const packLabel = isPdp && productPack.label ? productPack.label : 'bulk bags (850kg)';
    qtyMap['grav-kg'] = kg;
    qtyMap['grav-bags'] = packs;
    qtyMap['product-packs'] = packs;
    gR.innerHTML =
      `<span>Volume: <strong>${vol.toFixed(2)} m\u00b3</strong></span>` +
      `<span>Weight (+10%): <strong>${kg.toLocaleString('en-GB')} kg</strong></span>` +
      (isPdp && productPack.kg
        ? dbmCalcPackResultLine(packs, productPack)
        : `<span>You need: <strong>${packs.toLocaleString('en-GB')}</strong> ${packLabel}</span>`);
  }

  const fL = section.querySelector('[data-floor-length]');
  const fW = section.querySelector('[data-floor-width]');
  const fC = section.querySelector('[data-floor-coverage]');
  const fR = section.querySelector('[data-floor-result]');
  if (fL && fW && fC && fR) {
    const area = (parseFloat(fL.value) || 0) * (parseFloat(fW.value) || 0);
    const coverage = parseFloat(fC.value) || 2.2;
    const packs = Math.ceil((area / coverage) * 1.1);
    qtyMap['floor-packs'] = packs;
    qtyMap['product-packs'] = packs;
    fR.innerHTML = `<span>Area: <strong>${area.toFixed(2)} m\u00b2</strong></span><span>Packs (+10%): <strong>${packs}</strong></span>`;
  }

  dbmCalcApplyQtys(section, qtyMap);

  const useBtn = section.querySelector('[data-calc-use-qty]');
  if (useBtn) {
    const key = useBtn.dataset.calcQtyKey || 'product-packs';
    const qty = qtyMap[key] || 0;
    useBtn.hidden = qty < 1;
    useBtn.dataset.calcResolvedQty = String(qty);
  }
}

async function dbmCalcRefreshCartBar() {
  const bar = document.querySelector('dbm-calc-cart-bar');
  if (!bar) return;
  try {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) return;
    const cart = await res.json();
    const countEl = bar.querySelector('[data-calc-cart-count]');
    const totalEl = bar.querySelector('[data-calc-cart-total]');
    if (countEl) countEl.textContent = cart.item_count;
    if (totalEl) totalEl.textContent = DBM.formatMoney(cart.total_price, cart.currency);
    bar.hidden = cart.item_count === 0;
  } catch (_e) { /* noop */ }
}

async function dbmCalcAddUpsellRow(row, openDrawer) {
  const variantId = row.dataset.calcVariantId;
  const qty = parseInt(row.dataset.calcQty, 10) || 1;
  if (!variantId || qty < 1) return;
  const btn = row.querySelector('[data-calc-add]');
  const prev = btn ? btn.textContent : '';
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Adding\u2026';
  }
  try {
    await DBM.addToCart({ variantId, quantity: qty, source: 'calc-upsell', openDrawer });
    if (btn) btn.textContent = 'Added \u2713';
    dbmCalcRefreshCartBar();
  } catch (_e) {
    if (btn) btn.textContent = 'Try again';
  } finally {
    if (btn) {
      setTimeout(() => {
        btn.disabled = false;
        if (btn.textContent === 'Added \u2713') btn.textContent = 'Add';
        else if (btn.textContent === 'Try again') btn.textContent = prev || 'Add';
      }, 1800);
    }
  }
}

if (!customElements.get('dbm-calc-tabs')) {
  customElements.define(
    'dbm-calc-tabs',
    class DbmCalcTabs extends HTMLElement {
      connectedCallback() {
        const section = this.closest('.dbm-calc');
        if (!section) return;
        this.querySelectorAll('[data-tab]').forEach((tab) => {
          tab.addEventListener('click', () => {
            const target = tab.dataset.tab;
            this.querySelectorAll('[data-tab]').forEach((t) => {
              const active = t === tab;
              t.classList.toggle('is-active', active);
              t.setAttribute('aria-selected', active ? 'true' : 'false');
            });
            section.querySelectorAll('[data-panel]').forEach((p) => {
              const show = p.dataset.panel === target;
              p.classList.toggle('is-active', show);
              p.hidden = !show;
            });
            dbmRunCalc(section);
          });
        });
      }
    }
  );
}

document.addEventListener('input', (event) => {
  const section = event.target.closest && event.target.closest('.dbm-calc');
  if (!section) return;
  dbmRunCalc(section);
});

document.addEventListener('click', async (event) => {
  const addBtn = event.target.closest && event.target.closest('[data-calc-add]');
  if (addBtn) {
    const row = addBtn.closest('[data-calc-upsell]');
    if (row) {
      event.preventDefault();
      await dbmCalcAddUpsellRow(row, false);
    }
    return;
  }

  const addAllBtn = event.target.closest && event.target.closest('[data-calc-add-all]');
  if (addAllBtn) {
    const wrap = addAllBtn.closest('[data-calc-upsells]');
    const section = addAllBtn.closest('.dbm-calc');
    if (wrap && section) {
      event.preventDefault();
      addAllBtn.disabled = true;
      const rows = [...wrap.querySelectorAll('[data-calc-upsell]')].filter(
        (r) => (parseInt(r.dataset.calcQty, 10) || 0) > 0
      );
      for (const row of rows) {
        await dbmCalcAddUpsellRow(row, false);
      }
      addAllBtn.disabled = false;
      addAllBtn.textContent = 'Added to basket \u2713';
      setTimeout(() => {
        addAllBtn.textContent = 'Add all materials to basket';
      }, 2000);
    }
    return;
  }

  const useQtyBtn = event.target.closest && event.target.closest('[data-calc-use-qty]');
  if (useQtyBtn) {
    event.preventDefault();
    const qty = parseInt(useQtyBtn.dataset.calcResolvedQty, 10) || 0;
    if (qty < 1) return;
    const pdpInput =
      document.querySelector('.product-form__quantity .quantity__input') ||
      document.querySelector('.product-form input[name="quantity"]') ||
      document.querySelector('quantity-input .quantity__input');
    if (!pdpInput) return;
    pdpInput.value = qty;
    pdpInput.dispatchEvent(new Event('change', { bubbles: true }));
    pdpInput.dispatchEvent(new Event('input', { bubbles: true }));
    useQtyBtn.textContent = `Quantity set to ${qty}`;
    setTimeout(() => {
      useQtyBtn.textContent = 'Use this quantity';
    }, 1800);
    return;
  }

  const cartOpen = event.target.closest && event.target.closest('[data-calc-cart-open]');
  if (cartOpen) {
    event.preventDefault();
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }
});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.dbm-calc input, .dbm-calc select').forEach((input) => {
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });
  dbmCalcRefreshCartBar();
});

/* Mobile quick-quantity preset chips on PDP */
if (!customElements.get('dbm-qty-presets')) {
  customElements.define(
    'dbm-qty-presets',
    class DbmQtyPresets extends HTMLElement {
      connectedCallback() {
        const targetSel = this.dataset.qtyTarget;
        if (!targetSel) return;
        this.querySelectorAll('[data-qty-preset]').forEach((chip) => {
          chip.addEventListener('click', () => {
            const input = document.querySelector(targetSel);
            if (!input) return;
            const qty = parseInt(chip.dataset.qtyPreset, 10);
            if (!qty) return;
            input.value = qty;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            this.querySelectorAll('[data-qty-preset]').forEach((c) => {
              c.classList.toggle('is-active', c === chip);
            });
          });
        });
      }
    }
  );
}
